import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { LayoutGrid, Pencil, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import MobileCrudList from '../../component/common/MobileCrud';
import { KitField, KitSelect } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import StatusChip from '../../component/common/StatusChip';
import DialogForm from '../../component/dialog/DialogForm';
import FilterToolbar from '../../component/filter/FilterToolbar';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import Form from './Form';

// Categorias de produto e de serviço vivem em tabelas separadas no backend,
// mas a listagem é unificada e paginada no servidor por `GET /category/list`
// (`page`, `page_size`, `type`, `search` → {items, total, total_subcategories,
// page, page_size}). Tipo e busca por nome são filtrados no servidor.
const PAGE_SIZE_OPTIONS = [5, 8, 15];
const SEARCH_DEBOUNCE_MS = 400;
const MOBILE_PAGE_SIZE = 20;

const GridIcon = protoIcon('grid');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

function normalizeCategory(c) {
    return {
        id: c.id,
        name: c.name,
        description: c.description,
        type: c.type,
        margin: c.type === 'produto' ? c.profit_margin : null,
        subcategoriesCount: c.count_subs || 0,
    };
}

export default function List() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação.
    const [mobilePage, setMobilePage] = useState(0);
    const [draftType, setDraftType] = useState('');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // Paginação server-side: `page` é 0-based (DefaultTable); a API usa 1-based.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [totalSubcategories, setTotalSubcategories] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);

    const [typeFilter, setTypeFilter] = useState('');
    const [search, setSearch] = useState('');
    // Busca efetivamente enviada ao servidor (debounce sobre `search`).
    const [appliedSearch, setAppliedSearch] = useState('');

    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetCategoryId, setSheetCategoryId] = useState(null);
    const [sheetCategoryType, setSheetCategoryType] = useState('produto');

    const fetchData = () => setReloadKey((k) => k + 1);

    // Debounce da busca: ao digitar, aplica depois de uma pausa e volta à página 1.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search);
            setPage(0);
            setMobilePage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // Busca a página atual no servidor sempre que filtros/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (typeFilter) params.type = typeFilter;
        if (appliedSearch.trim()) params.search = appliedSearch.trim();

        setLoading(true);
        httpClient.get('/category/list', { params })
            .then((response) => {
                if (cancelled) return;
                const items = Array.isArray(response.data?.items) ? response.data.items : [];
                const total = response.data?.total || 0;
                // Página ficou além do fim (ex.: excluiu o último item da última página).
                if (!isMobileShell && items.length === 0 && total > 0 && page > 0) {
                    setPage(Math.ceil(total / pageSize) - 1);
                    return;
                }
                if (isMobileShell && requestPage > 0) {
                    setRows((prev) => [...prev, ...items.map(normalizeCategory).filter((row) => !prev.some((p) => p.id === row.id && p.type === row.type))]);
                } else {
                    setRows(items.map(normalizeCategory));
                }
                setTotalCount(total);
                setTotalSubcategories(response.data?.total_subcategories || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Erro ao carregar categorias:', error);
                setRows([]);
                setTotalCount(0);
                setTotalSubcategories(0);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, typeFilter, appliedSearch, reloadKey, isMobileShell, mobilePage]);

    const handleTypeFilterChange = (value) => {
        setTypeFilter(value);
        setPage(0);
        setMobilePage(0);
    };

    const handleSearchRemove = () => {
        setSearch('');
        setAppliedSearch('');
        setPage(0);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const handleClearFilters = () => {
        setTypeFilter('');
        setSearch('');
        setAppliedSearch('');
        setPage(0);
        setMobilePage(0);
    };

    const closeFormSheet = () => {
        setSheetOpen(false);
        setSheetCategoryId(null);
    };

    // `opts.keepOpen` — usado pelo CRUD de subcategorias dentro do drawer
    // (`Form.js`): salvar/editar/excluir uma subcategoria não deve fechar o
    // drawer da categoria (o usuário pode querer cadastrar várias em
    // sequência), só a lista de categorias atrás precisa ser recarregada
    // (para refletir a nova contagem de subcategorias). Salvar/editar a
    // categoria em si fecha o drawer normalmente.
    const handleFormSheetSuccess = (opts = {}) => {
        fetchData();
        if (!opts.keepOpen) {
            closeFormSheet();
        }
    };

    const handleFormError = (msg) => {
        setSeverity('error');
        setMessage(msg || 'Erro ao salvar categoria');
        setOpenSnackbar(true);
    };

    const handleOpenNew = () => {
        if (isMobileShell) {
            navigate(`/categories/new?type=${typeFilter || 'produto'}`);
            return;
        }
        setSheetCategoryId(null);
        setSheetCategoryType(typeFilter || 'produto');
        setSheetOpen(true);
    };

    const handleOpenEdit = (row) => {
        if (isMobileShell) {
            navigate(`/categories/edit/${row.id}?type=${row.type}`);
            return;
        }
        setSheetCategoryId(row.id);
        setSheetCategoryType(row.type);
        setSheetOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const endpoint = deleteTarget.type === 'produto'
                ? `/category/${deleteTarget.id}`
                : `/category-service/${deleteTarget.id}`;
            const response = await httpClient.delete(endpoint);
            if (response.status === 200) {
                setSeverity('success');
                setMessage('Categoria excluída com sucesso');
                setOpenSnackbar(true);
                setOpenDialogDelete(false);
                setDeleteTarget(null);
                fetchData();
            }
        } catch (error) {
            console.error('Erro ao excluir categoria:', error);
            setSeverity('error');
            setMessage(error.response?.data || error.message || 'Erro ao excluir categoria');
            setOpenSnackbar(true);
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            name: 'Nome',
            path: 'name',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }} title={row.name}>
                    {row.name}
                </Typography>
            ),
        },
        {
            name: 'Tipo',
            path: 'type',
            render: (row) => <StatusChip domain="itemType" value={row.type} />,
        },
        {
            name: 'Margem de lucro',
            path: 'margin',
            render: (row) => (
                row.type === 'produto' ? (
                    <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {row.margin !== undefined && row.margin !== null ? `${row.margin}%` : '—'}
                    </Typography>
                ) : (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                )
            ),
        },
        {
            name: 'Subcategorias',
            path: 'subcategoriesCount',
            render: (row) => (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px' }}>
                    <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                        {row.subcategoriesCount}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                        {row.subcategoriesCount === 1 ? 'subcategoria' : 'subcategorias'}
                    </Typography>
                </Box>
            ),
        },
    ];

    const actions = [
        ...(hasPermission('category.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => handleOpenEdit(row),
                tooltip: 'Editar',
            },
        }] : []),
        ...(hasPermission('category.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    setDeleteTarget(row);
                    setOpenDialogDelete(true);
                },
                tooltip: 'Excluir',
            },
        }] : []),
    ];

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), mesmo padrão
    // de `pages/services/List.js`/`pages/sales/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    const typeFilterLabel = typeFilter === 'produto' ? 'Produto' : typeFilter === 'servico' ? 'Serviço' : 'Todos';

    // Shell mobile: lista do protótipo `categorias/lista.html` (mesmos dados, filtros e ações).
    if (isMobileShell) {
        const canEdit = hasPermission('category.update');
        const canDelete = hasPermission('category.delete');
        const plural = (n) => `${n} subcategoria${n === 1 ? '' : 's'}`;
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget={'Categorias'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
                <MobileCrudList
                    items={rows}
                    getKey={(row) => `${row.type}-${row.id}`}
                    getRow={(row) => ({
                        icon: GridIcon,
                        tone: row.type === 'produto' ? undefined : 'purple',
                        title: row.name,
                        sub: row.type === 'produto'
                            ? `${plural(row.subcategoriesCount)} · margem ${row.margin !== undefined && row.margin !== null ? row.margin : 0}%`
                            : `${plural(row.subcategoriesCount)} · serviço`,
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Buscar categoria…"
                    filter={{
                        active: Boolean(typeFilter),
                        onOpen: () => setDraftType(typeFilter),
                        onApply: () => handleTypeFilterChange(draftType),
                        onClear: handleClearFilters,
                        children: (
                            <KitField label="Tipo">
                                <KitSelect value={draftType} onChange={(e) => setDraftType(e.target.value)} aria-label="Tipo">
                                    <option value="">Todos</option>
                                    <option value="produto">Produto</option>
                                    <option value="servico">Serviço</option>
                                </KitSelect>
                            </KitField>
                        ),
                    }}
                    emptyIcon={GridIcon}
                    emptyTitle="Nenhuma categoria encontrada"
                    emptyText="Ajuste a busca ou os filtros para ver outras categorias."
                    hasMore={rows.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    fabLabel={hasPermission('category.create') ? 'Nova' : undefined}
                    onNew={handleOpenNew}
                    rowTitle={(row) => row.name}
                    rowActions={(row) => [
                        ...(canEdit ? [{ label: 'Editar', icon: EditIcon, onClick: () => handleOpenEdit(row) }] : []),
                        ...(canDelete ? [{
                            label: 'Excluir',
                            icon: TrashIcon,
                            destructive: true,
                            onClick: () => {
                                setDeleteTarget(row);
                                setOpenDialogDelete(true);
                            },
                        }] : []),
                    ]}
                />
                <DialogForm
                    open={openDialogDelete}
                    iconType="warning"
                    onClose={() => { setOpenDialogDelete(false); setDeleteTarget(null); }}
                    onSubmit={handleDelete}
                    title="Excluir Categoria"
                    content={`Tem certeza que deseja excluir a categoria "${deleteTarget?.name || ''}"?`}
                    secondaryContent="Esta ação não pode ser desfeita. A categoria e todas as suas subcategorias serão excluídas permanentemente."
                    confirmButtonText="Confirmar Exclusão"
                    cancelButtonText="Cancelar"
                    loading={deleting}
                    loadingText="Excluindo..."
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Categorias'} message={message} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} severity={severity}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação "Nova categoria". */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <LayoutGrid size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Categorias
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Organize produtos e serviços em categorias e subcategorias.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('category.create') && (
                        <NewItemButton label="Nova categoria" onClick={handleOpenNew} />
                    )}
                </Box>

                {/* report-toolbar — Tipo/Buscar. */}
                <FilterToolbar
                    plain
                    activeCount={(typeFilter ? 1 : 0) + (appliedSearch ? 1 : 0)}
                    onClear={handleClearFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 220px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome da categoria…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Button
                            variant="text"
                            onClick={handleClearFilters}
                            sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                        >
                            Limpar
                        </Button>
                    )}
                >
                        <Box>
                            <TbLabel>Tipo</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={typeFilter}
                                    displayEmpty
                                    onChange={(e) => handleTypeFilterChange(e.target.value)}
                                    renderValue={() => typeFilterLabel}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: '9px' }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="produto">Produto</MenuItem>
                                    <MenuItem value="servico">Serviço</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                </FilterToolbar>

                {/* chips-row — filtros ativos removíveis + contagem/total de subcategorias. */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                    {typeFilter && (
                        <Chip
                            label={`Tipo: ${typeFilterLabel}`}
                            size="small"
                            onDelete={() => handleTypeFilterChange('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                        />
                    )}
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleSearchRemove}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} categoria${totalCount === 1 ? '' : 's'} encontrada${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`Total de subcategorias: ${totalSubcategories}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    />
                </Stack>

                <DefaultTable
                    colunms={columns}
                    data={rows}
                    iconsActions={actions}
                    onRowClick={hasPermission('category.update') ? handleOpenEdit : undefined}
                    loading={loading}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    itemLabel="categorias"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            <DialogForm
                open={openDialogDelete}
                iconType="warning"
                onClose={() => { setOpenDialogDelete(false); setDeleteTarget(null); }}
                onSubmit={handleDelete}
                title="Excluir Categoria"
                content={`Tem certeza que deseja excluir a categoria "${deleteTarget?.name || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita. A categoria e todas as suas subcategorias serão excluídas permanentemente."
                confirmButtonText="Confirmar Exclusão"
                cancelButtonText="Cancelar"
                loading={deleting}
                loadingText="Excluindo..."
            />

            {/* Drawer de add/edit — DESIGN-SYSTEM.md §5.6, largura fixa de
                `prototipo-lista-categorias.html` (480px). */}
            <Drawer
                anchor="right"
                open={sheetOpen}
                onClose={closeFormSheet}
                PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}
            >
                {sheetOpen && (
                    <Form
                        embedded
                        categoryId={sheetCategoryId}
                        categoryType={sheetCategoryType}
                        onClose={closeFormSheet}
                        onSuccess={handleFormSheetSuccess}
                        onError={handleFormError}
                    />
                )}
            </Drawer>
        </PageScaffold>
    );
}
